import {
  PermPlacementsListingGetters,
  PermPlacementsListingActions,
  PermPlacementsListingMutations
} from '@/app/features/perm-placements/features/perm-placements-listing/perm-placements-listing.keys';
import { permPlacementsListingService as perms } from '@/app/features/perm-placements/features/perm-placements-listing/perm-placements-listing.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { TolMessagesActions } from '@/app/features/tol-messages/tol-messages.keys';
import i18n from '@/i18n';

export const getInitialState = () => {
  return {
    permPlacementsLoading: true,
    permPlacements: [],
    permPlacementsFilteredRecordCount: 0,
    searchParam: null,
    permPlacementsFilters: {
      dateRangeFilters: {
        startDateMin: null,
        startDateMax: null
      },
      statusFilters: {
        draft: false,
        submitted: false,
        processed: false,
        finished: false,
        rejected: false,
        cancelled: false,
        obsolete: false
      }
    },
    permPlacementsSortOrder: {
      sortBy: 'status',
      sortDirection: 'desc'
    },
    permPlacementsPaginationParameters: {
      recordsPerPage: 10,
      offset: 0
    },
    permPlacementsBulkAction: null,
    modifiedPermPlacements: {}
  };
};

export const permPlacementsListingStore = {
  state: getInitialState(),
  getters: {
    [PermPlacementsListingGetters.SelectedPermPlacements]: state => param => {
      const selectedPermPlacements = state.permPlacements.filter(
        permPlacement => permPlacement.isSelected === true
      );
      return selectedPermPlacements.map(permPlacement => permPlacement[param]);
    },
    [PermPlacementsListingGetters.PermPlacementsFilters]: state => {
      return state.permPlacementsFilters;
    },
    [PermPlacementsListingGetters.PermPlacements]: state => {
      return state.permPlacements;
    },
    [PermPlacementsListingGetters.PermPlacementsFilteredRecordCount]: state => {
      return state.permPlacementsFilteredRecordCount;
    },
    [PermPlacementsListingGetters.PermPlacementsListingOffset]: state => {
      return state.permPlacementsPaginationParameters.offset;
    },
    [PermPlacementsListingGetters.PermPlacementsPerPage]: state => {
      return state.permPlacementsPaginationParameters.recordsPerPage;
    },
    [PermPlacementsListingGetters.PermPlacementsSortBy]: state => {
      return state.permPlacementsSortOrder.sortBy;
    },
    [PermPlacementsListingGetters.PermPlacementsSortDirection]: state => {
      return state.permPlacementsSortOrder.sortDirection;
    },
    [PermPlacementsListingGetters.PermPlacementsSortParams]: state => {
      return state.permPlacementsSortOrder;
    },
    [PermPlacementsListingGetters.PermPlacementsPaginationParams]: state => {
      return state.permPlacementsPaginationParameters;
    },
    [PermPlacementsListingGetters.ModifiedPermPlacements]: state => state.modifiedPermPlacements,
    [PermPlacementsListingGetters.PermPlacementIsLoading]: state => state.permPlacementsLoading
  },
  mutations: {
    [PermPlacementsListingMutations.SetPermPlacementsLoading]: (state, loading = true) => {
      state.permPlacementsLoading = loading;
    },
    [PermPlacementsListingMutations.SetPermPlacementsList]: (state, perms) => {
      state.permPlacements = perms;
    },
    [PermPlacementsListingMutations.SetPermPlacementsFilteredRecordCount]: (state, recordCount) => {
      state.permPlacementsFilteredRecordCount = recordCount;
    },
    [PermPlacementsListingMutations.SetPermPlacementsSortOrder]: (state, sortOrder) => {
      state.permPlacementsSortOrder = sortOrder;
    },
    [PermPlacementsListingMutations.SetPermPlacementsFilters]: (state, filters) => {
      state.permPlacementsFilters = filters;
    },
    [PermPlacementsListingMutations.ResetPermPlacementsPaginationOffset]: state => {
      state.permPlacementsPaginationParameters.offset = 0;
    },
    [PermPlacementsListingMutations.SetPermPlacementsPaginationParameters]: (state, pagination) => {
      state.permPlacementsPaginationParameters = pagination;
    },
    [PermPlacementsListingMutations.SetSearchParam]: (state, param) => {
      state.searchParam = param;
    },
    [PermPlacementsListingMutations.SelectPermPlacement]: (state, referenceNumber) => {
      state.permPlacements = state.permPlacements.map(permPlacement => {
        if (permPlacement.reference_number === referenceNumber) {
          permPlacement['isSelected'] = permPlacement.isSelected ? !permPlacement.isSelected : true;
        }
        return permPlacement;
      });
    }
  },
  actions: {
    [PermPlacementsListingActions.RetrievePermPlacements]: ({ state, dispatch, commit }) => {
      commit(PermPlacementsListingMutations.SetPermPlacementsLoading);
      return perms
        .retrievePermPlacements(
          state.permPlacementsFilters,
          state.permPlacementsSortOrder,
          state.permPlacementsPaginationParameters
        )
        .then(result => {
          dispatch(PermPlacementsListingActions.RetrievePermPlacementsFilteredRecordCount);
          commit(PermPlacementsListingMutations.SetPermPlacementsList, result);
        })
        .then(() => {
          commit(PermPlacementsListingMutations.SetPermPlacementsLoading, false);
        });
    },
    [PermPlacementsListingActions.RetrievePermPlacementsFilteredRecordCount]: ({
      state,
      commit,
      dispatch
    }) => {
      return perms
        .retrieveFilteredRecordCount(state.permPlacementsFilters)
        .then(recordCount => {
          commit(PermPlacementsListingMutations.SetPermPlacementsFilteredRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('permPlacement.listing.errors.retrievePerms')
          });
          commit(PermPlacementsListingMutations.SetPermPlacementsLoading, false);
        });
    },
    [PermPlacementsListingActions.SetPermPlacementsSortOrder]: (
      { commit, dispatch },
      sortOrder
    ) => {
      commit(PermPlacementsListingMutations.SetPermPlacementsSortOrder, sortOrder);
      commit(PermPlacementsListingMutations.ResetPermPlacementsPaginationOffset);
      dispatch(PermPlacementsListingActions.RetrievePermPlacements);
    },
    [PermPlacementsListingActions.SetPermPlacementsFilters]: ({ commit, dispatch }, filters) => {
      commit(PermPlacementsListingMutations.SetPermPlacementsFilters, filters);
      commit(PermPlacementsListingMutations.ResetPermPlacementsPaginationOffset);
      dispatch(PermPlacementsListingActions.RetrievePermPlacements);
    },
    [PermPlacementsListingActions.SetPermPlacementsPaginationParameters]: (
      { commit, dispatch },
      pagination
    ) => {
      commit(PermPlacementsListingMutations.SetPermPlacementsPaginationParameters, pagination);
      dispatch(PermPlacementsListingActions.RetrievePermPlacements);
    },
    [PermPlacementsListingActions.FilterListBySearchKey]: ({ commit, dispatch }, params) => {
      commit(PermPlacementsListingMutations.SetSearchParam, params.searchParam);
      if (!params.isSearchKey) {
        dispatch(PermPlacementsListingActions.RetrievePermPlacements);
      } else {
        commit(PermPlacementsListingMutations.ResetPermPlacementsPaginationOffset);
        commit(PermPlacementsListingMutations.SetPermPlacementsList, params.filteredItems);
        commit(
          PermPlacementsListingMutations.SetPermPlacementsFilteredRecordCount,
          params.filteredItems.length
        );
      }
    },
    [PermPlacementsListingActions.SelectPermPlacement]: ({ commit }, referenceNumber) => {
      commit(PermPlacementsListingMutations.SelectPermPlacement, referenceNumber);
    },
    [PermPlacementsListingActions.UpdatePermPlacements]: ({ getters, dispatch }, params) => {
      const selectedPermPlacements = getters.SelectedPermPlacements('reference_number');
      return perms
        .doPermPlacementAction(selectedPermPlacements, params.action, params.comment)
        .then(status => {
          if (status.message === 'success') {
            dispatch(PermPlacementsListingActions.RetrievePermPlacements);
            if (Object.keys(status.info).length > 0) {
              dispatch(TolMessagesActions.SetMessages, status.info);
            }
          } else {
            const messages = Object.values(status.errors);
            let items = [];
            for (let item of messages) {
              items = items.concat(item);
            }
            dispatch(TolErrorsActions.SetError, { items });
          }
        });
    }
  }
};
